import { CronJob, CronJobHealth } from 'types/cronJob'
import { InputOptions } from 'types/input'

type BadgeColor = 'green' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple'

// Label and badge colour for each health value
export const HEALTH_INFO: Record<
  CronJobHealth,
  { label: string; color: BadgeColor }
> = {
  healthy: { label: 'Healthy', color: 'green' },
  notRunYet: { label: 'Not run yet', color: 'gray' },
  overdue: { label: 'Overdue', color: 'yellow' },
  failing: { label: 'Failing', color: 'red' },
  longRunning: { label: 'Running long', color: 'orange' },
  setupError: { label: 'Not scheduled', color: 'purple' },
  inactive: { label: 'Inactive', color: 'gray' },
}

// Order of the summary cards, most serious first
export const HEALTH_ORDER: CronJobHealth[] = [
  'setupError',
  'failing',
  'longRunning',
  'overdue',
  'notRunYet',
  'healthy',
  'inactive',
]

// URL query key holding the page's filters (useLogFilters convention)
export const FILTER_FIELD_CRON_JOBS = 'cronJobFilters'

// 'NONE' clears a filter (handled by useLogFilters)
const NONE_OPTION: InputOptions = { label: '--- NONE ---', value: 'NONE' }

export const ACTIVE_OPTIONS: InputOptions[] = [
  NONE_OPTION,
  { label: 'Enabled', value: 'enabled' },
  { label: 'Disabled', value: 'disabled' },
]

export const getGroupOptions = (groups: string[]): InputOptions[] => [
  NONE_OPTION,
  ...groups.map((group) => ({ label: group, value: group })),
]

export const STATUS_OPTIONS: InputOptions[] = [
  NONE_OPTION,
  { label: 'Idle', value: 'idle' },
  { label: 'Running', value: 'running' },
]

export const HEALTH_OPTIONS: InputOptions[] = [
  NONE_OPTION,
  ...HEALTH_ORDER.map((health) => ({
    label: HEALTH_INFO[health].label,
    value: health,
  })),
]

// "45 min", "3 h 5 min", "2 d 4 h" - short enough for a table cell
export const formatMinutes = (minutes?: number) => {
  if (minutes === undefined || minutes === null) return '-'
  if (!Number.isFinite(minutes)) return '-'
  if (minutes < 1) return '<1 min'
  if (minutes < 60) return `${minutes} min`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} h ${minutes % 60} min`

  return `${Math.floor(hours / 24)} d ${hours % 24} h`
}

export const formatDuration = (ms?: number) => {
  if (ms === undefined || ms === null) return '-'
  if (ms < 60000) return `${Math.round(ms / 1000)} s`

  return formatMinutes(Math.round(ms / 60000))
}

// Same reason text the API saves for jobs that are never scheduled
export const MANUAL_ONLY_REASON = 'Manual trigger only (no schedule)'

export const isManualOnly = (job: CronJob) =>
  job.inactiveReason === MANUAL_ONLY_REASON
